/**
 * The optional name on a rule group.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Click-to-edit rather than a permanent input: most groups stay unnamed, and a
 * row of empty text boxes would read as unfinished work. Unnamed shows the
 * positional fallback ("Rule 2") in muted text, so the card always has a
 * heading to point at while a name stays clearly optional.
 */

import { type FC, useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';

import { cn } from '@/lib/utils';

export const RuleGroupTitle: FC<{
  name?: string;
  /** 1-based position, for the fallback label. */
  index: number;
  onChange: (name: string | undefined) => void;
}> = ({ name, index, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setDraft(name ?? ''), [name]);
  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = () => {
    // Blank clears the name rather than storing "" — the serializer drops it
    // and the card falls back to "Rule N".
    onChange(draft.trim() || undefined);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            setDraft(name ?? '');
            setEditing(false);
          }
        }}
        maxLength={60}
        placeholder={`Rule ${index}`}
        aria-label="Rule group name"
        className="h-7 min-w-0 flex-1 rounded border border-input bg-background px-1.5 text-[13.5px] font-semibold focus:outline-none focus:ring-2 focus:ring-ring"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title={name ? 'Rename this rule group' : 'Give this rule group a name'}
      aria-label={name ? `Rename ${name}` : `Name rule group ${index}`}
      className={cn(
        'inline-flex min-w-0 items-center gap-1.5 rounded px-1 py-0.5 text-[13.5px] font-semibold',
        // The pencil and the dashed underline stay VISIBLE rather than waiting
        // for a hover: unnamed, this control is a plain word that looks exactly
        // like the static caption it replaced, and nobody clicks a caption.
        'border-b border-dashed border-border/70 hover:border-primary/60 hover:bg-muted',
        name ? 'text-foreground' : 'text-muted-foreground',
      )}
    >
      <span className="truncate">{name || `Rule ${index}`}</span>
      <Pencil className="h-3 w-3 shrink-0 opacity-50" aria-hidden />
    </button>
  );
};

export default RuleGroupTitle;
